/**
 * DocumentList Component
 * 
 * Componente para listar documentos em grid com paginação e filtros.
 * 
 * Funcionalidades:
 * - Lista documentos em grid
 * - Paginação
 * - Filtros por categoria, status de expiração
 * - Empty state
 */

import { useState, useEffect } from 'react'
import { FileText } from 'lucide-react'
import DocumentCard from './DocumentCard'
import './DocumentList.css'

function DocumentList({
  documents = [],
  loading = false,
  onView,
  onEdit,
  onDelete,
  onPageChange,
  currentPage = 1,
  totalPages = 1,
  totalItems = 0,
  itemsPerPage = 12,
  filters = {},
  onFilterChange,
  className = ''
}) {
  const [selectedCategory, setSelectedCategory] = useState(filters.category_id || '')
  const [selectedExpiryStatus, setSelectedExpiryStatus] = useState(filters.expiry_status || '')

  useEffect(() => {
    if (onFilterChange) {
      const newFilters = {}
      if (selectedCategory) newFilters.category_id = selectedCategory
      if (selectedExpiryStatus) newFilters.expiry_status = selectedExpiryStatus
      onFilterChange(newFilters)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory, selectedExpiryStatus])

  const handlePreviousPage = () => {
    if (currentPage > 1 && onPageChange) {
      onPageChange(currentPage - 1)
    }
  }

  const handleNextPage = () => {
    if (currentPage < totalPages && onPageChange) {
      onPageChange(currentPage + 1)
    }
  }

  if (loading) {
    return (
      <div className={`document-list ${className}`}>
        <div className="document-list-loading">
          <p>Carregando documentos...</p>
        </div>
      </div>
    )
  }

  if (documents.length === 0) {
    return (
      <div className={`document-list ${className}`}>
        <div className="document-list-empty">
          <div className="document-list-empty-icon" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af' }}>
            <FileText size={48} />
          </div>
          <p className="document-list-empty-text">Nenhum documento encontrado</p>
        </div>
      </div>
    )
  }

  return (
    <div className={`document-list ${className}`}>
      {filters.showFilters && (
        <div className="document-list-filters">
          {filters.categories && filters.categories.length > 0 && (
            <select
              className="document-list-filter"
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
            >
              <option value="">Todas as categorias</option>
              {filters.categories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
          )}

          <select
            className="document-list-filter"
            value={selectedExpiryStatus}
            onChange={(e) => setSelectedExpiryStatus(e.target.value)}
          >
            <option value="">Todos os status</option>
            <option value="expired">Vencidos</option>
            <option value="urgent">Vence em breve (≤ 7 dias)</option>
            <option value="warning">Vence em breve (≤ 30 dias)</option>
            <option value="valid">Válidos</option>
            <option value="no_expiry">Sem expiração</option>
          </select>
        </div>
      )}

      <div className="document-list-grid">
        {documents.map((document) => (
          <DocumentCard
            key={document.id}
            document={document}
            onView={onView}
            onEdit={onEdit}
            onDelete={onDelete}
          />
        ))}
      </div>

      {totalPages > 1 && (
        <div className="document-list-pagination">
          <button
            className="document-list-pagination-button"
            onClick={handlePreviousPage}
            disabled={currentPage === 1}
          >
            ← Anterior
          </button>
          
          <span className="document-list-pagination-info">
            Página {currentPage} de {totalPages} ({totalItems} {totalItems === 1 ? 'documento' : 'documentos'})
          </span>

          <button
            className="document-list-pagination-button"
            onClick={handleNextPage}
            disabled={currentPage === totalPages}
          >
            Próxima →
          </button>
        </div>
      )}
    </div>
  )
}

export default DocumentList

